import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronRight, Mail, Menu, MessageCircle, Phone, X } from "lucide-react";
import { SeoManager } from "./SeoManager";
import { PublicScrollToTop } from "./PublicScrollToTop";
import { getPrimaryContactAction, normalizePhoneHref, normalizeWhatsappNumber } from "../lib/contact";

const navLinkClass = ({ isActive }) =>
  `ink-link text-sm font-semibold tracking-[0.18em] uppercase transition-colors ${
    isActive ? "text-[#111111]" : "text-[#666666] hover:text-[#111111]"
  }`;

export const SiteLayout = ({ data, ui, locale, setLocale, localeOptions }) => {
  const { navigation, contact, instagram, showroom } = data;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const visibleNavigation = navigation.filter((item) => item.path !== "/aliados");
  const socialLinks = [
    { id: "instagram", label: "Instagram", symbol: "◎", href: instagram.profile },
    { id: "facebook", label: "Facebook", symbol: "f", href: "https://www.facebook.com/aristamasvertice" },
    { id: "linkedin", label: "LinkedIn", symbol: "in", href: "https://www.linkedin.com/company/aristamasvertice" },
    { id: "pinterest", label: "Pinterest", symbol: "p", href: "https://www.pinterest.com/aristamasvertice" },
    { id: "threads", label: "Threads", symbol: "@", href: "https://www.threads.net/@aristamasvertice" },
  ];
  const legalNotice = ui.layout.legalParagraphs;
  const primaryAction = getPrimaryContactAction(contact, {
    whatsappMessage: "Hola, me interesa conocer las colecciones de Arista + Vértice. ¿Podrían compartirme información comercial y opciones para mi proyecto?",
    emailSubject: `${contact?.region_label || "Arista + Vértice"} — solicitud comercial`,
    emailBody: "Hola, me interesa conocer las colecciones de Arista + Vértice. ¿Podrían compartirme información comercial y opciones para mi proyecto?",
  });
  const headerPrimaryAction = contact?.header_cta_href
    ? {
        ...primaryAction,
        href: contact.header_cta_href,
        target: contact.header_cta_href.startsWith("http") ? "_blank" : primaryAction.target,
        rel: contact.header_cta_href.startsWith("http") ? "noreferrer" : primaryAction.rel,
      }
    : primaryAction;
  const salesWhatsappNumber = normalizeWhatsappNumber(contact?.sales_whatsapp);
  const supportWhatsappNumber = normalizeWhatsappNumber(contact?.support_whatsapp);
  const officePhoneHref = normalizePhoneHref(contact?.office_phone);
  const mobilePrimaryIcon = headerPrimaryAction.type === "email" ? Mail : headerPrimaryAction.type === "phone" ? Phone : MessageCircle;
  const MobilePrimaryIcon = mobilePrimaryIcon;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111]">
      <PublicScrollToTop />
      <SeoManager data={data} locale={locale} />
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto grid max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-6 px-6 py-3 md:px-12 md:py-3.5">
          <Link
            to="/"
            className="flex items-center"
            data-testid="header-brand-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/336sf0vp_AristaLogos-01.png"
              alt="Arista + Vértice"
              className="h-9 w-auto max-w-[160px] object-contain md:h-10 md:max-w-[220px]"
              data-testid="header-brand-logo"
            />
          </Link>

          <nav className="hidden items-center justify-center gap-5 lg:flex">
            {visibleNavigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={navLinkClass}
                data-testid={`header-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center justify-end gap-3">
            <a
              href={headerPrimaryAction.href}
              target={headerPrimaryAction.target}
              rel={headerPrimaryAction.rel}
              className="hidden items-center gap-2 rounded-full bg-[#111111] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#FAFAFA] transition hover:bg-[#2B2B2B] lg:inline-flex"
              data-testid="header-primary-contact-button"
            >
              <span>{contact.header_cta_label}</span>
            </a>

            <label className="hidden items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#666666] xl:flex" data-testid="header-locale-selector-wrapper">
              <span>{ui.layout.regionLabel}</span>
              <select
                value={locale}
                onChange={(event) => setLocale(event.target.value)}
                className="bg-transparent pr-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#111111] outline-none"
                data-testid="header-locale-selector"
              >
                {localeOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {headerPrimaryAction.href ? (
              <a
                href={headerPrimaryAction.href}
                target={headerPrimaryAction.target}
                rel={headerPrimaryAction.rel}
                className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition lg:hidden ${
                  headerPrimaryAction.type === "whatsapp"
                    ? "border-[#25D366]/20 bg-[#25D366]/10 text-[#159347] hover:border-[#25D366] hover:bg-[#25D366] hover:text-white"
                    : "border-black/10 bg-white text-[#111111] hover:border-[#111111] hover:bg-[#111111] hover:text-[#FAFAFA]"
                }`}
                aria-label={contact.header_cta_label}
                data-testid="mobile-primary-contact-button"
              >
                <MobilePrimaryIcon size={18} />
              </a>
            ) : null}

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((current) => !current)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] transition hover:border-[#111111] lg:hidden"
              aria-label={isMobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden border-t border-black/5 px-6 transition-all duration-300 ease-out lg:hidden ${
            isMobileMenuOpen ? "max-h-[32rem] translate-y-0 py-4 opacity-100" : "max-h-0 -translate-y-2 py-0 opacity-0"
          }`}
          data-testid="mobile-menu-panel"
        >
          <div className={`mx-auto max-w-7xl space-y-4 transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}>
              <label className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#666666]" data-testid="mobile-locale-selector-wrapper">
                <span>{ui.layout.regionLabel}</span>
                <select
                  value={locale}
                  onChange={(event) => setLocale(event.target.value)}
                  className="bg-transparent text-[11px] font-semibold uppercase tracking-[0.14em] text-[#111111] outline-none"
                  data-testid="mobile-locale-selector"
                >
                  {localeOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="grid gap-2 pb-1" data-testid="mobile-menu-list">
                {visibleNavigation.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between rounded-[1.2rem] border px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] transition-colors ${
                        isActive
                          ? "border-[#111111] bg-[#111111] text-[#FAFAFA]"
                          : "border-black/10 bg-white text-[#111111]"
                      }`
                    }
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    <span>{item.label}</span>
                    <ChevronRight size={16} />
                  </NavLink>
                ))}
              </div>
          </div>
        </div>
      </header>

      <div
        key={location.pathname}
        className="page-transition-shell"
        data-testid="page-transition-shell"
      >
        <Outlet />
      </div>

      <footer className="border-t border-black/5 bg-white/80">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:px-12 lg:grid-cols-[1.15fr_1.2fr_0.75fr]">
          <div className="space-y-4">
            <span
              className="text-xs font-extrabold uppercase tracking-[0.32em] text-[#666666]"
              data-testid="footer-brand-kicker"
            >
              Arista + Vértice
            </span>
            <h2 className="max-w-xl text-3xl font-medium tracking-tight" data-testid="footer-brand-title">
              {ui.layout.footerBrandTitle}
            </h2>
            <p className="max-w-xl text-sm leading-7 text-[#666666]" data-testid="footer-showroom-address">
              {showroom.description}
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#666666]" data-testid="footer-contact-label">
              {ui.layout.contactLabel}
            </p>
            <div className="space-y-1.5 text-[0.95rem] leading-5 text-[#111111] md:text-[1rem]" data-testid="footer-contact-details-list">
              <p className="font-medium tracking-[-0.01em]" data-testid="footer-contact-region">⌂ {contact.region_label}</p>
              <a href={`mailto:${contact.main_email}`} className="block break-words" data-testid="footer-contact-main-email">
                ✉ {contact.main_email}
              </a>
              {contact.support_email ? (
                <a href={`mailto:${contact.support_email}`} className="block break-words" data-testid="footer-contact-support-email">
                  ✉ {contact.support_email}
                </a>
              ) : null}
              <p className="font-medium tracking-[-0.01em]" data-testid="footer-contact-showroom-name">⌖ {contact.footer_showroom_name}</p>
              <p className="leading-5 text-[#444444]" data-testid="footer-contact-address-detail">
                {showroom.address}
              </p>
              <p className="leading-5 text-[#666666]" data-testid="footer-contact-hours-detail">
                {showroom.hours}
              </p>
              {officePhoneHref ? (
                <a href={`tel:${officePhoneHref}`} className="block break-words" data-testid="footer-contact-office-phone">
                  ☎ {ui.detailPage.contact.office}: {contact.office_phone}
                </a>
              ) : null}
              {primaryAction.type === "whatsapp" && salesWhatsappNumber ? (
                <a
                  href={primaryAction.href}
                  target={primaryAction.target}
                  rel={primaryAction.rel}
                  className="block"
                  data-testid="footer-contact-primary-action"
                >
                  ☏ {contact.sales_label}: {contact.sales_whatsapp.replace(/^\+\d{2}\s?/, "")}
                </a>
              ) : null}
              {primaryAction.type === "email" ? (
                <a href={primaryAction.href} className="block break-words" data-testid="footer-contact-primary-action">
                  ✉ {contact.sales_label}: {primaryAction.value}
                </a>
              ) : null}
              {primaryAction.type === "phone" ? (
                <a href={primaryAction.href} className="block break-words" data-testid="footer-contact-primary-action">
                  ☎ {contact.sales_label}: {primaryAction.value}
                </a>
              ) : null}
              {supportWhatsappNumber && contact.support_label ? (
                <a
                  href={`https://wa.me/${supportWhatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                  data-testid="footer-contact-management-whatsapp"
                >
                  ☏ {contact.support_label}: {contact.support_whatsapp.replace(/^\+\d{2}\s?/, "")}
                </a>
              ) : null}
              <Link to="/contacto" className="inline-flex items-center gap-2 pt-1 text-sm font-semibold" data-testid="footer-contact-page-link">
                {ui.layout.contactPageLabel} <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#666666]" data-testid="footer-social-label">
              {ui.layout.socialLabel}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="footer-social-buttons-group">
              {socialLinks.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.label}
                  title={item.label}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-sm font-semibold text-[#111111] transition hover:-translate-y-0.5 hover:border-[#111111] hover:bg-[#111111] hover:text-[#FAFAFA]"
                  data-testid={`footer-social-${item.id}-button`}
                >
                  <span className="tracking-[-0.04em]">{item.symbol}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <section className="border-t border-black/5 bg-white/80 px-6 py-10 text-[#444444] md:px-12 md:py-12" data-testid="legal-notice-section">
        <div className="mx-auto max-w-[88rem] space-y-4 text-center">
          <h2
            className="text-[13px] font-medium uppercase leading-[1.02] tracking-[0.22em] text-[#666666]"
            data-testid="legal-notice-title"
          >
            {ui.layout.legalTitle}
          </h2>

          <div className="space-y-2">
            {legalNotice.map((paragraph, index) => (
              <p
                key={paragraph}
                className="mx-auto max-w-[78rem] text-[13px] leading-[1.02] text-[#555555]"
                data-testid={`legal-notice-paragraph-${index + 1}`}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};